"use strict";

exports.__esModule = true;
exports.MetricValueBatchProvider = MetricValueBatchProvider;
var _react = _interopRequireWildcard(require("react"));
var _Metrics = require("../../../Catalogs/Metrics");
var _customHooks = require("../../../Utils/customHooks");
var _DataAccessorContext = require("../../API/DataInterface/DataAccessorContext");
var _ConfigurableLayout = require("../../ConfigurableLayout");
var _SelectionContext = require("../../Core/SelectionContext");
var _PlantStateActivities = require("../../PlantStateActivities");
var _TimePeriodController = require("../../Time/TimePeriodController/TimePeriodController");
var _ReportingPeriodContext = require("../../Time/ReportingPeriod/ReportingPeriodContext");
var _TimeContext = require("../../Time/TimeContext");
var _MetricValueBatchContext = require("./MetricValueBatchContext");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function _interopRequireWildcard(e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, "default": e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (var _t in e) "default" !== _t && {}.hasOwnProperty.call(e, _t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, _t)) && (i.get || i.set) ? o(f, _t, i) : f[_t] = e[_t]); return f; })(e, t); }
var CHUNK_SIZE = 25;
var SCROLL_SETTLE_MS = 250;
function valueKey(deviceUuid, metricName, interval) {
  return deviceUuid + "\0" + metricName + "\0" + interval.startMillis + "\0" + interval.endMillis;
}
function metricKey(deviceUuid, metricName) {
  return deviceUuid + "\0" + metricName;
}
function metricKeyFromValueKey(key) {
  var second = key.indexOf("\0", key.indexOf("\0") + 1);
  return second === -1 ? null : key.slice(0, second);
}
function sourceName(source) {
  return source && source.type === "metric" && source.name ? source.name : null;
}
function uuidsOf(devices) {
  return (devices || []).map(function (device) {
    return device && device.uuid;
  }).filter(Boolean);
}
function chunkUuids(uuids) {
  var chunks = [];
  for (var i = 0; i < uuids.length; i += CHUNK_SIZE) {
    chunks.push(uuids.slice(i, i + CHUNK_SIZE));
  }
  return chunks;
}

/**
 * Historic metrics for the cells on screen, plus historic color metrics.
 * Color uses the reporting-period interval, which is what DeviceMetricColorSource queries.
 * The realtime flags match the ones DeviceColorController passes through.
 */
function historicRequests(_ref) {
  var elements = _ref.elements,
    backgroundColorSource = _ref.backgroundColorSource,
    reportingPeriod = _ref.reportingPeriod,
    selection = _ref.selection,
    time = _ref.time,
    layoutSettings = _ref.layoutSettings;
  var workday = time && time.currentWorkday && time.currentWorkday.interval;
  var shift = time && time.currentShift && time.currentShift.interval;
  var reportingInterval = reportingPeriod && reportingPeriod.interval;
  if (!workday || !shift || !reportingInterval) {
    return [];
  }
  var seen = new Set();
  var requests = [];
  var add = function add(metricName, interval) {
    if (!metricName || !interval || interval.startMillis == null || interval.endMillis == null) {
      return;
    }
    if ((0, _Metrics.canSubscribeToRealtime)(workday, shift, interval)) {
      return;
    }
    var key = metricName + "\0" + interval.startMillis + "\0" + interval.endMillis;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    requests.push({
      metricName: metricName,
      interval: interval
    });
  };
  var backgroundIsHistoric = layoutSettings && layoutSettings.colorSource && layoutSettings.colorSource.realtime === false;
  var textIsHistoric = layoutSettings && layoutSettings.backgroundColorSource && layoutSettings.backgroundColorSource.realtime === false;
  var list = Array.isArray(elements) ? elements : [];
  for (var i = 0; i < list.length; i++) {
    var element = list[i];
    if (!element || element.type !== "MetricValue") {
      continue;
    }
    var elementProps = element.props || {};
    var period = (0, _TimePeriodController.getTimePeriod)(reportingPeriod, selection, elementProps.dataRange);
    add(elementProps.metricName, period && period.interval);
    if (backgroundIsHistoric) {
      add(sourceName(elementProps.backgroundColorSource), reportingInterval);
    }
    if (textIsHistoric) {
      add(sourceName(elementProps.colorSource), reportingInterval);
    }
  }
  if (backgroundIsHistoric) {
    add(sourceName(backgroundColorSource), reportingInterval);
  }
  return requests;
}
function useRefreshToken() {
  var plantStateFilter = (0, _react.useContext)(_PlantStateActivities.PlantStateFilterContext);
  var refreshInterval = plantStateFilter && plantStateFilter.refreshInterval;
  var shouldRefresh = Boolean(plantStateFilter && plantStateFilter.shouldRefresh);
  var _useState = (0, _react.useState)(0),
    token = _useState[0],
    setToken = _useState[1];
  var bump = (0, _react.useCallback)(function () {
    return setToken(function (current) {
      return current + 1;
    });
  }, []);
  (0, _customHooks.useInterval)({
    interval: refreshInterval,
    callbackFunction: bump
  });
  var wasRefreshing = (0, _react.useRef)(false);
  (0, _react.useEffect)(function () {
    if (shouldRefresh && !wasRefreshing.current) {
      bump();
    }
    wasRefreshing.current = shouldRefresh;
  }, [shouldRefresh, bump]);
  return token;
}
function mergeStore(prev, signature, patch, failedKeys) {
  var changed = prev.signature !== signature;
  var values = changed ? {} : Object.assign({}, prev.values);
  // A new date or metric set must not keep the last number. Refresh keeps it,
  // because that path does not change the signature and leaves values in place.
  var previous = changed ? {} : Object.assign({}, prev.previous);
  var remember = function remember(key, value) {
    values[key] = value;
    var deviceMetric = metricKeyFromValueKey(key);
    if (deviceMetric) {
      previous[deviceMetric] = value;
    }
  };
  if (patch) {
    var keys = Object.keys(patch);
    for (var i = 0; i < keys.length; i++) {
      remember(keys[i], patch[keys[i]]);
    }
  }
  if (failedKeys) {
    for (var _i = 0; _i < failedKeys.length; _i++) {
      var key = failedKeys[_i];
      if (values[key] !== undefined) {
        continue;
      }
      var deviceMetric = metricKeyFromValueKey(key);
      if (deviceMetric && previous[deviceMetric] !== undefined) {
        continue;
      }
      remember(key, null);
    }
  }
  return {
    signature: signature,
    values: values,
    previous: previous
  };
}
function patchFromResult(request, deviceUuids, result) {
  var patch = {};
  for (var i = 0; i < deviceUuids.length; i++) {
    patch[valueKey(deviceUuids[i], request.metricName, request.interval)] = null;
  }
  var rows = result && result.by_device || [];
  for (var _i2 = 0; _i2 < rows.length; _i2++) {
    var row = rows[_i2];
    if (!row || row.key == null) {
      continue;
    }
    patch[valueKey(row.key, request.metricName, request.interval)] = row.value != null ? row.value : null;
  }
  return patch;
}
function queryChunk(accessor, request, deviceUuids) {
  var query = (0, _Metrics.buildMetricQuery)([request.metricName]);
  query.filterByDevice(deviceUuids);
  query.filterByRange(new Date(request.interval.startMillis).toISOString(), new Date(request.interval.endMillis).toISOString());
  query.aggregateBy(["device"]);
  return accessor.getMetrics(query.getQuery());
}

/**
 * Loads historic Metric Value data for the devices currently on screen.
 * Each metric is its own request. A chunk paints as soon as it returns.
 */
function MetricValueBatchProvider(_ref2) {
  var devices = _ref2.devices,
    visibleDevices = _ref2.visibleDevices,
    elements = _ref2.elements,
    backgroundColorSource = _ref2.backgroundColorSource,
    children = _ref2.children;
  var time = (0, _react.useContext)(_TimeContext.TimeContext);
  var reportingPeriod = (0, _react.useContext)(_ReportingPeriodContext.ReportingPeriodContext);
  var selection = (0, _react.useContext)(_SelectionContext.SelectionContext);
  var layoutSettings = (0, _react.useContext)(_ConfigurableLayout.LayoutSettingsContext);
  var refreshToken = useRefreshToken();
  var dataAccessor = (0, _react.useContext)(_DataAccessorContext.DataAccessorContext);
  var dataAccessorRef = (0, _react.useRef)(dataAccessor);
  dataAccessorRef.current = dataAccessor;
  var deviceUuids = uuidsOf(devices);
  var visibleSource = visibleDevices == null ? devices : visibleDevices;
  var visibleDeviceUuids = [];
  var seenVisible = new Set();
  var visibleList = uuidsOf(visibleSource);
  var deviceUuidSet = new Set(deviceUuids);
  for (var i = 0; i < visibleList.length; i++) {
    var uuid = visibleList[i];
    if (deviceUuidSet.has(uuid) && !seenVisible.has(uuid)) {
      seenVisible.add(uuid);
      visibleDeviceUuids.push(uuid);
    }
  }
  var requests = historicRequests({
    elements: elements,
    backgroundColorSource: backgroundColorSource,
    reportingPeriod: reportingPeriod,
    selection: selection,
    time: time,
    layoutSettings: layoutSettings
  });
  var requestSignature = JSON.stringify(requests.map(function (request) {
    return {
      metricName: request.metricName,
      startMillis: request.interval.startMillis,
      endMillis: request.interval.endMillis
    };
  }));
  var visibleSignature = JSON.stringify(visibleDeviceUuids);
  var deviceSignature = JSON.stringify(deviceUuids);
  var coveredKeySet = (0, _react.useMemo)(function () {
    var keys = new Set();
    for (var _i3 = 0; _i3 < requests.length; _i3++) {
      var request = requests[_i3];
      for (var d = 0; d < deviceUuids.length; d++) {
        keys.add(valueKey(deviceUuids[d], request.metricName, request.interval));
      }
    }
    return keys;
    // signatures capture the devices and requests used above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestSignature, deviceSignature]);
  var _useState2 = (0, _react.useState)({
      signature: null,
      values: {},
      previous: {}
    }),
    store = _useState2[0],
    setStore = _useState2[1];
  var requestSignatureRef = (0, _react.useRef)(requestSignature);
  requestSignatureRef.current = requestSignature;
  var loadedSignatureRef = (0, _react.useRef)(null);
  var requestedRef = (0, _react.useRef)(new Set());
  var keyGenerationRef = (0, _react.useRef)(new Map());
  var appliedRefreshRef = (0, _react.useRef)(refreshToken);
  var mountedRef = (0, _react.useRef)(true);
  (0, _react.useEffect)(function () {
    return function () {
      mountedRef.current = false;
    };
  }, []);
  (0, _react.useEffect)(function () {
    var accessor = dataAccessorRef.current;
    var signatureAtStart = requestSignature;
    var metricSetChanged = loadedSignatureRef.current !== signatureAtStart;
    if (metricSetChanged) {
      loadedSignatureRef.current = signatureAtStart;
      requestedRef.current = new Set();
      keyGenerationRef.current = new Map();
      setStore(function () {
        return {
          signature: signatureAtStart,
          values: {},
          previous: {}
        };
      });
    }
    var forceRefresh = appliedRefreshRef.current !== refreshToken;
    appliedRefreshRef.current = refreshToken;
    if (!accessor || !accessor.getMetrics || requests.length === 0 || visibleDeviceUuids.length === 0) {
      return undefined;
    }
    var applyOutcome = function applyOutcome(ok, result, request, deviceChunk, gens) {
      if (!mountedRef.current || requestSignatureRef.current !== signatureAtStart) {
        return;
      }
      var fresh = [];
      for (var _i4 = 0; _i4 < deviceChunk.length; _i4++) {
        var key = valueKey(deviceChunk[_i4], request.metricName, request.interval);
        if (keyGenerationRef.current.get(key) === gens[key]) {
          fresh.push(deviceChunk[_i4]);
        }
      }
      if (fresh.length === 0) {
        return;
      }
      setStore(function (prev) {
        return mergeStore(prev, signatureAtStart, ok ? patchFromResult(request, fresh, result) : null, ok ? null : fresh.map(function (uuid) {
          return valueKey(uuid, request.metricName, request.interval);
        }));
      });
    };
    var startRequests = function startRequests() {
      if (!mountedRef.current || requestSignatureRef.current !== signatureAtStart) {
        return;
      }
      var _loop = function _loop() {
        var request = requests[_i5];
        var todo = [];
        for (var d = 0; d < visibleDeviceUuids.length; d++) {
          var _uuid = visibleDeviceUuids[d];
          var key = valueKey(_uuid, request.metricName, request.interval);
          if (forceRefresh || !requestedRef.current.has(key)) {
            todo.push(_uuid);
            requestedRef.current.add(key);
          }
        }
        var chunks = chunkUuids(todo);
        var _loop2 = function _loop2() {
          var deviceChunk = chunks[c];
          var gens = {};
          for (var u = 0; u < deviceChunk.length; u++) {
            var _key = valueKey(deviceChunk[u], request.metricName, request.interval);
            var gen = (keyGenerationRef.current.get(_key) || 0) + 1;
            keyGenerationRef.current.set(_key, gen);
            gens[_key] = gen;
          }
          queryChunk(accessor, request, deviceChunk).then(function (result) {
            return applyOutcome(true, result, request, deviceChunk, gens);
          }, function () {
            return applyOutcome(false, null, request, deviceChunk, gens);
          });
        };
        for (var c = 0; c < chunks.length; c++) {
          _loop2();
        }
      };
      for (var _i5 = 0; _i5 < requests.length; _i5++) {
        _loop();
      }
    };
    if (metricSetChanged || forceRefresh) {
      startRequests();
      return undefined;
    }
    var timeoutId = setTimeout(startRequests, SCROLL_SETTLE_MS);
    return function () {
      return clearTimeout(timeoutId);
    };
    // requests and the visible list are captured by their signatures
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestSignature, visibleSignature, refreshToken]);
  var api = (0, _react.useMemo)(function () {
    var values = store.values,
      previous = store.previous;
    var rangeChanged = store.signature !== requestSignature;
    return {
      isCovered: function isCovered(deviceUuid, metricName, interval) {
        if (!interval) {
          return false;
        }
        return coveredKeySet.has(valueKey(deviceUuid, metricName, interval));
      },
      isLoading: function isLoading(deviceUuid, metricName, interval) {
        if (!interval) {
          return false;
        }
        var key = valueKey(deviceUuid, metricName, interval);
        if (!coveredKeySet.has(key)) {
          return false;
        }
        if (rangeChanged) {
          return true;
        }
        if (values[key] !== undefined) {
          return false;
        }
        return previous[metricKey(deviceUuid, metricName)] === undefined;
      },
      getValue: function getValue(deviceUuid, metricName, interval) {
        if (!interval || rangeChanged) {
          return undefined;
        }
        var key = valueKey(deviceUuid, metricName, interval);
        if (!coveredKeySet.has(key)) {
          return undefined;
        }
        if (values[key] !== undefined) {
          return values[key];
        }
        var prior = previous[metricKey(deviceUuid, metricName)];
        return prior === undefined ? undefined : prior;
      }
    };
  }, [coveredKeySet, requestSignature, store]);
  if (coveredKeySet.size === 0) {
    return /*#__PURE__*/_react["default"].createElement(_MetricValueBatchContext.MetricValueBatchContext.Provider, {
      value: _MetricValueBatchContext.EMPTY_METRIC_VALUE_BATCH
    }, children);
  }
  return /*#__PURE__*/_react["default"].createElement(_MetricValueBatchContext.MetricValueBatchContext.Provider, {
    value: api
  }, children);
}